import isEmpty from "lodash/isEmpty";
import sumBy from "lodash/sumBy";

import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  AssemblyWithChildren,
  createKeyFromDescription,
  OptimizedMachineTimes,
  round,
  withOptimizedMachineTimes,
} from "shared";

export const getProcessStepSummary = (
  quoteRequestLines: AssemblyWithChildren<QuoteRequestLineDto>[],
  optimizedMachineTimes: OptimizedMachineTimes,
) => {
  if (isEmpty(quoteRequestLines)) {
    return [];
  }

  const allProcessSteps = quoteRequestLines.flatMap((child) =>
    child.quoteRequestLine.processSteps.map((rawProcessStep) => {
      const processStep = withOptimizedMachineTimes(rawProcessStep, optimizedMachineTimes);
      const quantity = child.quoteRequestLine.part.quantityInAssembly ?? 1;
      return {
        ...processStep,
        otherMachineTime: (processStep.otherMachineTime ?? 0) * quantity,
        cycleTimeCost: (processStep.cycleTimeCost ?? 0) * quantity,
        fixedCost: (processStep.fixedCost ?? 0) * quantity,
        fixedCostPerSeries: (processStep.fixedCostPerSeries ?? 0) * quantity,
        manTime: (processStep.manTime ?? 0) * quantity,
      };
    }),
  );

  const groupedProcessStepsMap = new Map<string, typeof allProcessSteps>();

  allProcessSteps.forEach((processStep) => {
    const key = `${String(
      processStep.industrialProcess,
    )}_${createKeyFromDescription(processStep.description)}`;

    if (!groupedProcessStepsMap.has(key)) {
      groupedProcessStepsMap.set(key, [processStep]);
    } else {
      groupedProcessStepsMap.get(key)!.push(processStep);
    }
  });

  const groupedProcessSteps: Array<typeof allProcessSteps> = Array.from(
    groupedProcessStepsMap.values(),
  );
  const aggregatedProcessSteps = groupedProcessSteps.map((processSteps) => {
    const firstProcess = processSteps[0]!;

    const fieldsToSum = {
      otherMachineTime: round(
        sumBy(processSteps, (process) => process.otherMachineTime || 0),
        8,
      ),
      machinePreparationTime: round(
        sumBy(processSteps, (process) => process.machinePreparationTime || 0),
        8,
      ),
      manTime: round(
        sumBy(processSteps, (process) => process.manTime || 0),
        8,
      ),
      toolingCost: sumBy(processSteps, (process) => process.toolingCost || 0),
      fixedCost: sumBy(processSteps, (process) => process.fixedCost || 0),
      fixedCostPerSeries: sumBy(processSteps, (process) => process.fixedCostPerSeries || 0),
      cycleTimeCost: round(
        sumBy(processSteps, (process) => process.cycleTimeCost || 0),
        8,
      ),
      machinePreparationTimeCost: round(
        sumBy(processSteps, (process) => process.machinePreparationTimeCost || 0),
        8,
      ),
    };

    return {
      ...firstProcess,
      ...fieldsToSum,
    };
  });

  return aggregatedProcessSteps;
};
